import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  getOptimizelyEdgeExperiments,
  OPTIMIZELY_EDGE_COOKIE_NAME,
} from 'lib/utils/get-optimizely-edge-experiments';
import { trackEvent } from 'lib/analytics';

/*
  Adjust the EXPERIMENT_ROUTING_CONFIG object to define experiments and their variants.
  Each experiment should have a unique name, 
  a basePath (the URL path where the experiment runs (reroutes FROM)),
  and a variants object mapping variant names to their respective paths (where it reroutes TO).
  Only add to this object if the experiment needs to reroute on client side.
  Do not add to this object when the experiment only redirects on direct navigation
*/

export const EXPERIMENT_ROUTING_CONFIG = {
  // example_test_name: {
  //   basePath: '/example-path',
  //   variants: {
  //     variant_a: '/daily-synbiotic-em',
  //     variant_b: '/daily-synbiotic-em-2',
  //   },
  // },
};

export const useExperimentRouting = () => {
  const router = useRouter();
  const isRedirecting = useRef(false);

  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_EXPERIMENT_REROUTING !== 'true' ||
      Object.keys(EXPERIMENT_ROUTING_CONFIG).length === 0
    ) {
      return;
    }

    const handleRouteChangeStart = (url: string) => {
      // Skip if we're already in the middle of a redirect to prevent loops
      if (isRedirecting.current) {
        return;
      }
      try {
        const urlObj = new URL(url, window.location.origin);
        const targetPath = urlObj.pathname;

        // Determine if any experiments are running on that path
        const experimentConfig = Object.values(EXPERIMENT_ROUTING_CONFIG).find(
          (config) => config.basePath === targetPath,
        );

        if (!experimentConfig) {
          // return because no experiments found for that path
          return;
        }

        let experiments = getOptimizelyEdgeExperiments();
        const experimentName = Object.keys(EXPERIMENT_ROUTING_CONFIG).find(
          (name) => EXPERIMENT_ROUTING_CONFIG[name] === experimentConfig,
        );
        if (!experiments) {
          // No experiment cookie was found. Assign a variant and set the cookie
          if (!experimentName) return;

          // Get variant keys (excluding control)
          const variantKeys = Object.keys(experimentConfig.variants);
          const allVariants = ['control', ...variantKeys];
          // Randomly assign
          const randomIndex = Math.floor(Math.random() * allVariants.length);
          const assignedVariant = allVariants[randomIndex];
          document.cookie = `${OPTIMIZELY_EDGE_COOKIE_NAME}=${encodeURIComponent(JSON.stringify({ [experimentName]: assignedVariant }))}; path=/; max-age=${60 * 60 * 24 * 365}`;

          // Fire an Experiment Viewed event. This event normally happens on first page load
          // if the cookie was set at the edge, but since this is happening after routing,
          // we need to add the call here
          trackEvent('Experiment Viewed', {
            experimentName,
            variationName: assignedVariant,
          });
          // Set experiments to mimic what getOptimizelyEdgeExperiments would return
          experiments = { [experimentName]: assignedVariant };
        }

        if (!experimentName || !experiments[experimentName]) {
          return;
        }

        const userVariant = experiments[experimentName];
        const variantPath = experimentConfig.variants[userVariant];

        // If user has a variant that should redirect, and they're not already on that path
        if (variantPath && variantPath !== targetPath) {
          isRedirecting.current = true;

          const newUrl = variantPath + urlObj.search + urlObj.hash;

          // Use replace to avoid adding to browser history
          router.replace(newUrl).finally(() => {
            isRedirecting.current = false;
          });
        }
      } catch (error) {
        console.error('Error with experiment route change', error);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router]);
};

export const getExperimentVariant = (experimentName: string): string | null => {
  const experiments = getOptimizelyEdgeExperiments();
  return experiments?.[experimentName] || null;
};

export const isInExperimentVariant = (
  experimentName: string,
  variant: string,
): boolean => {
  return getExperimentVariant(experimentName) === variant;
};
